'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { toaster } from '@/components/ui/toaster';
import { api } from '../services/api';
import { AuthContext } from './AuthContext';
import { WishlistItemProps } from '@/types/Wishlist';
import { ResponseError } from '@/types/Error';

type WishlistContextProps = {
  items: WishlistItemProps[];
  wishlistIsLoading: boolean;
  isInWishlist: (productId: string, productOptId: string | null) => boolean;
  addItem: (
    productId: string,
    productOptId?: string | null,
    productOptLabel?: string | null,
  ) => Promise<void>;
  removeItem: (productId: string, productOptId?: string | null) => Promise<void>;
};

type WishlistProviderProps = {
  children: React.ReactNode;
};

export const WishlistContext = createContext({} as WishlistContextProps);

export function WishlistProvider({ children }: WishlistProviderProps) {
  const [items, setItems] = useState<WishlistItemProps[]>([]);
  const [wishlistIsLoading, setWishlistIsLoading] = useState(false);

  const { isAuthenticated } = useContext(AuthContext);

  const fetchWishlist = useCallback(async () => {
    try {
      setWishlistIsLoading(true);
      const response = await api.get('/customer-wishlist');
      setItems(response.data);
    } catch {
      setItems([]);
    } finally {
      setWishlistIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlist();
    } else {
      setItems([]);
    }
  }, [isAuthenticated, fetchWishlist]);

  const isInWishlist = useCallback(
    (productId: string, productOptId: string | null) =>
      items.some(
        (item) =>
          item.product.id === productId &&
          (item.product_opt_id ?? null) === (productOptId ?? null),
      ),
    [items],
  );

  const addItem = useCallback(
    async (
      productId: string,
      productOptId?: string | null,
      productOptLabel?: string | null,
    ) => {
      if (!isAuthenticated) {
        toaster.create({
          type: 'info',
          title: 'Inicie sessão para guardar nos favoritos',
        });
        return;
      }

      try {
        setWishlistIsLoading(true);
        const response = await api.post('/customer-wishlist', {
          product_id: productId,
          product_opt_id: productOptId || null,
          product_opt_label: productOptLabel || null,
        });

        await fetchWishlist();

        if (response.data) {
          toaster.create({
            type: 'success',
            title: 'Adicionado aos favoritos',
          });
        }
      } catch (e) {
        const err = e as ResponseError;
        toaster.create({
          type: 'error',
          title:
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'Erro ao adicionar aos favoritos',
        });
      } finally {
        setWishlistIsLoading(false);
      }
    },
    [isAuthenticated, fetchWishlist],
  );

  const removeItem = useCallback(
    async (productId: string, productOptId?: string | null) => {
      const existing = items.find(
        (item) =>
          item.product.id === productId &&
          (item.product_opt_id ?? null) === (productOptId ?? null),
      );

      if (!existing) return;

      try {
        setWishlistIsLoading(true);
        await api.delete(`/customer-wishlist/${existing.id}`);
        setItems((prev) => prev.filter((item) => item.id !== existing.id));
        toaster.create({
          type: 'success',
          title: 'Removido dos favoritos',
        });
      } catch (e) {
        const err = e as ResponseError;
        toaster.create({
          type: 'error',
          title:
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'Erro ao remover dos favoritos',
        });
      } finally {
        setWishlistIsLoading(false);
      }
    },
    [items],
  );

  return (
    <WishlistContext.Provider
      value={{
        items,
        wishlistIsLoading,
        isInWishlist,
        addItem,
        removeItem,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}
